import { supportedOrganizationLocale } from '../lib/organization-text/locales'
declare global {
  interface Window {
    __kindLumenEmbedLoaded?: boolean
    KindLumen?: {
      open?: (url: string, options?: { returnFocusTo?: HTMLElement | null }) => void
      version?: string
    }
  }
}

;(() => {
  if (window.__kindLumenEmbedLoaded) return
  window.__kindLumenEmbedLoaded = true

  const EMBED_STYLE_ID = 'kindlumen-embed-runtime'
  const DONATION_EMBED_SHELL_MAX_WIDTH = 1024
  const DONATION_EMBED_SHELL_MAX_HEIGHT = 700
  const POPUP_ATTR = 'data-kindlumen-popup'
  const INLINE_ATTR = 'data-kindlumen-inline'
  const FLOAT_ATTR = 'data-kindlumen-float'
  // Site-wide snippet: names the organization; the button and toast feed
  // come from KindLumen so the site never needs a new snippet.
  const SITE_ATTR = 'data-kindlumen-site'
  const MOUNTED_ATTR = 'data-kindlumen-mounted'
  // Reported to embedded frames in the host-ready handshake and on
  // window.KindLumen.version so support can tell which widget a site runs.
  const WIDGET_VERSION = '3'
  const DONATION_RESULT_PARAM = 'kindlumen_donation'
  // The toast cycles continuously while the donor is on the page: first toast
  // appears shortly after load, each stays visible for a few seconds, then a
  // short gap before the next donation. When the list is exhausted it loops.
  const SOCIAL_PROOF_FIRST_DELAY_MS = 4_000
  const SOCIAL_PROOF_MIN_GAP_MS = 5_000
  const SOCIAL_PROOF_MAX_GAP_MS = 9_000
  const SOCIAL_PROOF_VISIBLE_MS = 6_000
  // Baked at build time so the embed widget can talk to the Convex HTTP host
  // directly from any third-party page (no proxy on the host site needed).
  const CONVEX_SITE_URL: string =
    (import.meta as { env?: { VITE_CONVEX_SITE_URL?: string } }).env?.VITE_CONVEX_SITE_URL ?? ''

  // Floating Donate button options mirror src/lib/floating-button.ts.
  // floating-button.test.ts fails if the runtime and studio drift apart.
  // Bottom-left and bottom-center are deliberately not offered: that is where
  // the recent-donation toast lives, and a button there would push it up.
  const FLOAT_POSITIONS = ['top-left', 'top-center', 'top-right', 'left', 'right', 'bottom-right'] as const
  type FloatPosition = (typeof FLOAT_POSITIONS)[number]
  const FLOAT_DEFAULT_COLOR = '#2563EB'
  const FLOAT_ICON_SVG_ATTRIBUTES =
    'stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" style="display:block;width:1.2em;height:1.2em"'
  const FLOAT_ICON_GLYPHS: Record<string, { filled: boolean; markup: string }> = {
    heart: {
      filled: true,
      markup:
        '<path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"/>',
    },
    gift: {
      filled: false,
      markup:
        '<path d="M12 7v14"/><path d="M20 11v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8"/><path d="M7.5 7a1 1 0 0 1 0-5A4.8 8 0 0 1 12 7a4.8 8 0 0 1 4.5-5 1 1 0 0 1 0 5"/><rect x="3" y="7" width="18" height="4" rx="1"/>',
    },
    'hand-heart': {
      filled: false,
      markup:
        '<path d="M11 14h2a2 2 0 0 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 16"/><path d="m14.45 13.39 5.05-4.694C20.196 8 21 6.85 21 5.75a2.75 2.75 0 0 0-4.797-1.837.276.276 0 0 1-.406 0A2.75 2.75 0 0 0 11 5.75c0 1.2.802 2.248 1.5 2.946L16 11.95"/><path d="m2 15 6 6"/><path d="m7 20 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a1 1 0 0 0-2.75-2.91"/>',
    },
    sparkles: {
      filled: false,
      markup:
        '<path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"/><path d="M20 2v4"/><path d="M22 4h-4"/><circle cx="4" cy="20" r="2"/>',
    },
    star: {
      filled: true,
      markup:
        '<path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/>',
    },
    'hand-coins': {
      filled: false,
      markup:
        '<path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17"/><path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"/><path d="m2 16 6 6"/><circle cx="16" cy="9" r="2.9"/><circle cx="6" cy="5" r="3"/>',
    },
  }

  function ensureStyles() {
    if (document.getElementById(EMBED_STYLE_ID)) return

    const style = document.createElement('style')
    style.id = EMBED_STYLE_ID
    style.textContent = [
      '.kindlumen-modal-backdrop{position:fixed;inset:0;background:rgba(6,10,24,.52);backdrop-filter:blur(18px) saturate(140%);-webkit-backdrop-filter:blur(18px) saturate(140%);display:flex;align-items:center;justify-content:center;overflow-y:auto;padding:clamp(16px,4vh,36px) clamp(12px,2vw,24px);z-index:2147483646;opacity:0;transition:opacity .24s ease}',
      '.kindlumen-modal-backdrop.kindlumen-open{opacity:1}',
      `.kindlumen-modal-shell{position:relative;width:min(${DONATION_EMBED_SHELL_MAX_WIDTH}px,100%);height:min(${DONATION_EMBED_SHELL_MAX_HEIGHT}px,calc(100dvh - clamp(32px,8vh,72px)));min-height:min(${DONATION_EMBED_SHELL_MAX_HEIGHT}px,calc(100dvh - clamp(32px,8vh,72px)));opacity:0;transform:translateY(10px) scale(.98);transition:transform .28s cubic-bezier(.2,.8,.2,1),opacity .24s ease}`,
      '.kindlumen-modal-shell.kindlumen-open{opacity:1;transform:translateY(0) scale(1)}',
      // The frame is layered above the loader so its painted background covers
      // the spinner once content is ready. Until then we keep it at opacity 0
      // so the donor never sees a blank-white iframe mid-load.
      '.kindlumen-modal-frame{position:absolute;inset:0;width:100%;height:100%;border:0;background:transparent;box-shadow:none;opacity:0;transition:opacity .25s ease;z-index:2}',
      '.kindlumen-modal-shell.kindlumen-content-ready .kindlumen-modal-frame{opacity:1}',
      // Spinner overlay shown while the iframe loads. Sits on top of the
      // blurred backdrop (no background of its own) so the donor sees a clean
      // loading state instead of a white box.
      '.kindlumen-modal-loader{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;opacity:1;transition:opacity .25s ease;pointer-events:none;z-index:1}',
      '.kindlumen-modal-shell.kindlumen-content-ready .kindlumen-modal-loader{opacity:0}',
      '.kindlumen-modal-spinner{width:42px;height:42px;border-radius:50%;border:3px solid rgba(255,255,255,.32);border-top-color:#ffffff;animation:kindlumen-spin .85s linear infinite}',
      '@keyframes kindlumen-spin{to{transform:rotate(360deg)}}',
      // z-index:3 keeps the close button clickable above both the loader
      // (z-index:1) and the iframe (z-index:2) — without it the iframe's
      // stacking context covers the part of the button that sits inside
      // the shell, blocking the click.
      '.kindlumen-modal-close{position:absolute;top:-12px;right:-12px;display:flex;align-items:center;justify-content:center;width:36px;height:36px;border:1px solid rgba(255,255,255,.45);border-radius:999px;background:rgba(255,255,255,.95);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);color:#0e192c;font:600 16px/1 system-ui,sans-serif;cursor:pointer;box-shadow:0 10px 28px rgba(15,23,42,.24);z-index:3}',
      '@media (max-width:640px){.kindlumen-modal-backdrop{align-items:flex-start;padding:16px 10px}.kindlumen-modal-shell{height:calc(100dvh - 32px);min-height:calc(100dvh - 32px);width:100%}.kindlumen-modal-close{top:8px;right:8px}}',
      '.kindlumen-inline-frame{width:100%;min-height:560px;border:0;border-radius:0;background:transparent;display:block;transition:height .2s ease}',
      // Campaign cards are short; a smaller floor avoids a tall blank box
      // before the first resize lands.
      '.kindlumen-inline-frame.kindlumen-card-frame{min-height:180px}',
      '.kindlumen-inline-frame.kindlumen-sized{min-height:0}',
      // Thank-you toast shown on the org's website when the donor returns
      // from Stripe Checkout with ?kindlumen_donation=success.
      '.kindlumen-thanks-toast{position:fixed;top:24px;right:24px;left:24px;max-width:420px;margin-left:auto;display:flex;align-items:flex-start;gap:14px;padding:18px 20px;background:#fff;border:1px solid rgba(15,23,42,.08);border-radius:18px;box-shadow:0 22px 56px rgba(15,23,42,.18);z-index:2147483645;font:14px/1.5 system-ui,-apple-system,sans-serif;color:#0e192c;opacity:0;transform:translateY(-12px);transition:opacity .3s ease,transform .3s ease}',
      '.kindlumen-thanks-toast.kindlumen-open{opacity:1;transform:translateY(0)}',
      '.kindlumen-thanks-icon{display:flex;align-items:center;justify-content:center;flex:0 0 36px;width:36px;height:36px;border-radius:999px;background:#dcfce7;color:#15803d;font:600 18px/1 system-ui,sans-serif}',
      '.kindlumen-thanks-body{flex:1;min-width:0}',
      '.kindlumen-thanks-title{margin:0;font-weight:600;font-size:15px;color:#0e192c}',
      '.kindlumen-thanks-message{margin:4px 0 0;font-size:13px;line-height:1.5;color:#475569}',
      '.kindlumen-thanks-close{flex:0 0 auto;border:0;background:transparent;padding:4px;margin:-4px;cursor:pointer;color:#64748b;font:600 18px/1 system-ui,sans-serif;border-radius:6px}',
      '.kindlumen-thanks-close:hover{background:rgba(15,23,42,.06);color:#0e192c}',
      '@media (max-width:520px){.kindlumen-thanks-toast{top:16px;right:12px;left:12px}}',
      // Recent-donations social-proof toast — a small card that cycles donor
      // names, amounts, and locations with a Donate call-to-action. Slides up
      // from a bottom corner; low-key so it encourages without hijacking the
      // host site.
      '.kindlumen-proof-toast{position:fixed;bottom:24px;left:24px;max-width:360px;display:flex;align-items:center;gap:14px;padding:12px 14px 12px 16px;background:#fff;border:1px solid rgba(15,23,42,.06);border-radius:16px;box-shadow:0 22px 55px -12px rgba(15,23,42,.28),0 8px 20px -8px rgba(15,23,42,.18),0 1px 0 rgba(255,255,255,.9) inset;z-index:2147483644;font:13px/1.45 system-ui,-apple-system,sans-serif;color:#0e192c;opacity:0;transform:translateY(28px) scale(.94);transition:opacity .28s ease,transform .45s cubic-bezier(.34,1.56,.64,1)}',
      '.kindlumen-proof-toast.kindlumen-right{left:auto;right:24px}',
      // Entrance springs up from below with a double bounce; the base transform
      // above handles the exit (slide back down + fade).
      '.kindlumen-proof-toast.kindlumen-open{opacity:1;transform:translateY(0) scale(1);animation:kindlumen-proof-bounce .62s cubic-bezier(.22,1,.36,1) both}',
      '@keyframes kindlumen-proof-bounce{0%{opacity:0;transform:translateY(52px) scale(.92)}55%{opacity:1;transform:translateY(-14px) scale(1.015)}72%{transform:translateY(6px) scale(.998)}86%{transform:translateY(-3px) scale(1)}100%{opacity:1;transform:translateY(0) scale(1)}}',
      '.kindlumen-proof-body{flex:1;min-width:0}',
      '.kindlumen-proof-message{margin:0;font-size:14px;font-weight:700;color:#2563eb;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
      '.kindlumen-proof-balloon{margin-left:4px}',
      '.kindlumen-proof-meta{margin:3px 0 0;font-size:12px;color:#64748b;display:flex;align-items:center;gap:6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
      '.kindlumen-proof-flag{font-size:13px;line-height:1}',
      '.kindlumen-proof-cta{flex:0 0 auto;border:1px solid rgba(37,99,235,.5);background:#fff;color:#2563eb;font:600 13px/1 system-ui,-apple-system,sans-serif;padding:9px 18px;border-radius:999px;cursor:pointer;transition:background .15s ease,color .15s ease,border-color .15s ease}',
      '.kindlumen-proof-cta:hover{background:#2563eb;color:#fff;border-color:#2563eb}',
      '@media (max-width:520px){.kindlumen-proof-toast{left:12px;right:12px;bottom:16px;max-width:none}.kindlumen-proof-toast.kindlumen-right{left:12px;right:12px}}',
      // Floating Donate button (data-kindlumen-float). The host div is what
      // gets positioned (and centered with a transform); the <a> inside
      // carries the look plus the entrance/hover motion, so the two
      // transforms never fight. Sits under the modal and both toasts.
      // Colors and edge distances arrive as custom properties set inline by
      // mountFloat, so hover states and media queries can still use them.
      '.kindlumen-float-host{position:fixed;z-index:2147483643;margin:0;padding:0;pointer-events:none;--kl-float-gap-x:20px;--kl-float-gap-y:20px}',
      '.kindlumen-float-host a.kindlumen-float-button{pointer-events:auto;display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;margin:0;border:var(--kl-float-border,0);text-decoration:none!important;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;font-weight:600;line-height:1;letter-spacing:.01em;white-space:nowrap;cursor:pointer;-webkit-tap-highlight-color:transparent;border-radius:999px;background:var(--kl-float-bg);color:var(--kl-float-fg);box-shadow:0 10px 30px rgba(15,23,42,.22),0 2px 6px rgba(15,23,42,.12);opacity:0;transform:translateY(10px);transition:transform .2s ease,box-shadow .2s ease,opacity .35s ease,background .15s ease,color .15s ease}',
      '.kindlumen-float-rounded a.kindlumen-float-button{border-radius:12px}',
      '.kindlumen-float-square a.kindlumen-float-button{border-radius:4px}',
      '.kindlumen-float-font-inherit a.kindlumen-float-button{font-family:inherit}',
      '.kindlumen-float-no-shadow a.kindlumen-float-button{box-shadow:none}',
      // Slide in from the edge the button hugs.
      '.kindlumen-float-top-left a.kindlumen-float-button,.kindlumen-float-top-center a.kindlumen-float-button,.kindlumen-float-top-right a.kindlumen-float-button{transform:translateY(-10px)}',
      '.kindlumen-float-left a.kindlumen-float-button{transform:translateX(-10px)}',
      '.kindlumen-float-right a.kindlumen-float-button{transform:translateX(10px)}',
      '.kindlumen-float-host.kindlumen-float-in a.kindlumen-float-button{opacity:1;transform:none}',
      '.kindlumen-float-host.kindlumen-float-in a.kindlumen-float-button:hover{transform:translateY(-2px);background:var(--kl-float-hover-bg);color:var(--kl-float-hover-fg);box-shadow:0 16px 36px rgba(15,23,42,.26),0 3px 8px rgba(15,23,42,.14)}',
      '.kindlumen-float-no-shadow.kindlumen-float-in a.kindlumen-float-button:hover{box-shadow:none}',
      '.kindlumen-float-host.kindlumen-float-in.kindlumen-float-top-left a.kindlumen-float-button:hover,.kindlumen-float-host.kindlumen-float-in.kindlumen-float-top-center a.kindlumen-float-button:hover,.kindlumen-float-host.kindlumen-float-in.kindlumen-float-top-right a.kindlumen-float-button:hover{transform:translateY(2px)}',
      '.kindlumen-float-host.kindlumen-float-in.kindlumen-float-left a.kindlumen-float-button:hover{transform:translateX(2px)}',
      '.kindlumen-float-host.kindlumen-float-in.kindlumen-float-right a.kindlumen-float-button:hover{transform:translateX(-2px)}',
      // A tab is glued to its edge: no lift on hover (it would peel away from
      // the wall), only the shadow and color response. Must come after the
      // per-position hover rules above so it wins at equal specificity.
      '.kindlumen-float-host.kindlumen-float-in.kindlumen-float-tab a.kindlumen-float-button:hover{transform:none}',
      // Two-tone focus ring so it shows on light and dark host pages alike.
      '.kindlumen-float-host a.kindlumen-float-button:focus-visible{outline:0;box-shadow:0 0 0 3px #fff,0 0 0 6px #0f172a}',
      '.kindlumen-float-inner{display:inline-flex;align-items:center;gap:.55em}',
      '.kindlumen-float-icon{display:inline-flex;flex:0 0 auto}',
      // Label + optional second line. In vertical writing mode the column
      // stacks sideways, so the two lines run side by side along the edge.
      '.kindlumen-float-text{display:flex;flex-direction:column;align-items:flex-start;gap:.22em}',
      '.kindlumen-float-sublabel{font-size:.76em;font-weight:500;opacity:.85;letter-spacing:0}',
      '.kindlumen-float-sm a.kindlumen-float-button{font-size:14px;padding:10px 16px}',
      '.kindlumen-float-md a.kindlumen-float-button{font-size:15px;padding:13px 20px}',
      '.kindlumen-float-lg a.kindlumen-float-button{font-size:17px;padding:16px 26px}',
      // Positions. Bottom offsets clear the iPhone home indicator; the plain
      // `bottom` before each calc() is the fallback for browsers without env().
      '.kindlumen-float-top-left{top:var(--kl-float-gap-y);left:var(--kl-float-gap-x)}',
      '.kindlumen-float-top-center{top:var(--kl-float-gap-y);left:50%;transform:translateX(-50%)}',
      '.kindlumen-float-top-right{top:var(--kl-float-gap-y);right:var(--kl-float-gap-x)}',
      '.kindlumen-float-left{top:50%;left:var(--kl-float-gap-x);transform:translateY(-50%)}',
      '.kindlumen-float-right{top:50%;right:var(--kl-float-gap-x);transform:translateY(-50%)}',
      '.kindlumen-float-bottom-right{bottom:var(--kl-float-gap-y);bottom:calc(var(--kl-float-gap-y) + env(safe-area-inset-bottom,0px));right:var(--kl-float-gap-x)}',
      // Tabs hug the edge they belong to and square off that side.
      '.kindlumen-float-tab.kindlumen-float-left{left:0}',
      '.kindlumen-float-tab.kindlumen-float-right{right:0}',
      '.kindlumen-float-tab.kindlumen-float-top-left,.kindlumen-float-tab.kindlumen-float-top-center,.kindlumen-float-tab.kindlumen-float-top-right{top:0}',
      '.kindlumen-float-tab.kindlumen-float-bottom-right{bottom:0}',
      '.kindlumen-float-tab.kindlumen-float-left a.kindlumen-float-button{border-radius:0 14px 14px 0}',
      '.kindlumen-float-tab.kindlumen-float-right a.kindlumen-float-button{border-radius:14px 0 0 14px}',
      '.kindlumen-float-tab.kindlumen-float-top-left a.kindlumen-float-button,.kindlumen-float-tab.kindlumen-float-top-center a.kindlumen-float-button,.kindlumen-float-tab.kindlumen-float-top-right a.kindlumen-float-button{border-radius:0 0 14px 14px}',
      '.kindlumen-float-tab.kindlumen-float-bottom-right a.kindlumen-float-button{border-radius:14px 14px 0 0}',
      // Edge buttons stand upright: the label runs along the edge. On the left
      // the whole thing is turned so the text reads bottom-up, and the icon is
      // turned back so it stays the right way up.
      '.kindlumen-float-vertical .kindlumen-float-inner{writing-mode:vertical-rl}',
      '.kindlumen-float-flip .kindlumen-float-inner,.kindlumen-float-flip .kindlumen-float-icon{transform:rotate(180deg)}',
      '.kindlumen-float-vertical.kindlumen-float-sm a.kindlumen-float-button{padding:16px 10px}',
      '.kindlumen-float-vertical.kindlumen-float-md a.kindlumen-float-button{padding:20px 13px}',
      '.kindlumen-float-vertical.kindlumen-float-lg a.kindlumen-float-button{padding:26px 16px}',
      // Toasts keep their vertical spot and move sideways instead: a top-right
      // button makes the thank-you toast left-align (the proof toast swaps
      // corners in showProofToast). Phones have no sideways room, so there
      // the toasts stack above/below the button — unless it's hidden on phones.
      '.kindlumen-has-float-top-right .kindlumen-thanks-toast{margin-left:0;margin-right:auto}',
      // Tighter default gaps on phones; an org's own distances (set inline)
      // still win over this.
      '@media (max-width:640px){.kindlumen-float-host{--kl-float-gap-x:12px;--kl-float-gap-y:12px}.kindlumen-float-host.kindlumen-float-hide-mobile{display:none}}',
      '@media (max-width:520px){.kindlumen-has-float-bottom-right:not(.kindlumen-float-mobile-hidden) .kindlumen-proof-toast{bottom:88px}.kindlumen-has-float-top-left:not(.kindlumen-float-mobile-hidden) .kindlumen-thanks-toast,.kindlumen-has-float-top-center:not(.kindlumen-float-mobile-hidden) .kindlumen-thanks-toast,.kindlumen-has-float-top-right:not(.kindlumen-float-mobile-hidden) .kindlumen-thanks-toast{top:84px}}',
      '@media print{.kindlumen-float-host{display:none}}',
      '@media (prefers-reduced-motion:reduce){.kindlumen-float-host a.kindlumen-float-button{transition:none}}',
    ].join('')
    document.head.appendChild(style)
  }

  function formatCurrencyAmount(amountCents: number, currency: string = 'USD') {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(amountCents / 100)
    } catch {
      return `$${Math.round(amountCents / 100)}`
    }
  }

  type ProofDonation = {
    name: string | null
    amountCents: number
    location: string | null
    countryCode: string | null
    frequency?: string
  }

  type ProofPosition = 'bottom-left' | 'bottom-right'

  // Turn an ISO 3166-1 alpha-2 code into its flag emoji via regional-indicator
  // symbols. Returns '' for anything that isn't a clean two-letter code.
  function flagEmoji(countryCode: string | null): string {
    if (!countryCode || countryCode.length !== 2 || !/^[a-zA-Z]{2}$/.test(countryCode)) {
      return ''
    }
    const base = 0x1f1e6
    const upper = countryCode.toUpperCase()
    return (
      String.fromCodePoint(base + (upper.charCodeAt(0) - 65)) +
      String.fromCodePoint(base + (upper.charCodeAt(1) - 65))
    )
  }

  function buildProofMessage(donation: ProofDonation): string {
    const amount = formatCurrencyAmount(donation.amountCents)
    const who = donation.name ?? 'Someone'
    return `${who} donated ${amount}`
  }

  function showProofToast(donation: ProofDonation, position: ProofPosition, donateUrl: string | null) {
    ensureStyles()

    const existing = document.querySelector('[data-kindlumen-proof="true"]')
    if (existing) existing.remove()

    // A floating Donate button owns the bottom-right corner; the toast takes
    // the other corner rather than sitting on top of it or being pushed up.
    const floatInCorner = document.documentElement.classList.contains('kindlumen-has-float-bottom-right')
    const corner: ProofPosition = position === 'bottom-right' && floatInCorner ? 'bottom-left' : position

    const toast = document.createElement('div')
    toast.className = corner === 'bottom-right' ? 'kindlumen-proof-toast kindlumen-right' : 'kindlumen-proof-toast'
    toast.setAttribute('role', 'status')
    toast.setAttribute('aria-live', 'polite')
    toast.dataset.kindlumenProof = 'true'

    const body = document.createElement('div')
    body.className = 'kindlumen-proof-body'

    const message = document.createElement('p')
    message.className = 'kindlumen-proof-message'
    message.textContent = buildProofMessage(donation)
    const balloon = document.createElement('span')
    balloon.className = 'kindlumen-proof-balloon'
    balloon.textContent = '🎈'
    message.appendChild(balloon)
    body.appendChild(message)

    if (donation.location) {
      const meta = document.createElement('p')
      meta.className = 'kindlumen-proof-meta'
      const flag = flagEmoji(donation.countryCode)
      if (flag) {
        const flagEl = document.createElement('span')
        flagEl.className = 'kindlumen-proof-flag'
        flagEl.textContent = flag
        meta.appendChild(flagEl)
      }
      const locationEl = document.createElement('span')
      locationEl.textContent = donation.location
      meta.appendChild(locationEl)
      body.appendChild(meta)
    }

    toast.appendChild(body)

    if (donateUrl) {
      const cta = document.createElement('button')
      cta.type = 'button'
      cta.className = 'kindlumen-proof-cta'
      cta.textContent = 'Donate'
      cta.addEventListener('click', () => openModal(donateUrl))
      toast.appendChild(cta)
    }

    document.body.appendChild(toast)
    window.requestAnimationFrame(() => toast.classList.add('kindlumen-open'))

    window.setTimeout(() => {
      toast.classList.remove('kindlumen-open')
      window.setTimeout(() => toast.remove(), 320)
    }, SOCIAL_PROOF_VISIBLE_MS)
  }

  // The Donate button reuses whichever embed trigger the host placed on the
  // page (popup preferred, then the floating button, then inline) so the
  // toast opens the same form.
  function findDonateUrlOnPage(): string | null {
    const popup = document.querySelector(`[${POPUP_ATTR}]`)
    const popupUrl = popup?.getAttribute(POPUP_ATTR)
    if (popupUrl) return popupUrl
    const float = document.querySelector(`[${FLOAT_ATTR}]`)
    const floatUrl = float?.getAttribute(FLOAT_ATTR)
    if (floatUrl) return floatUrl
    const mountedFloat = document.querySelector<HTMLAnchorElement>('a.kindlumen-float-button[href]')
    if (mountedFloat?.href) return mountedFloat.href
    const inline = document.querySelector(`[${INLINE_ATTR}]`)
    const inlineUrl = inline?.getAttribute(INLINE_ATTR)
    if (inlineUrl) return toPopupUrl(inlineUrl)
    return null
  }

  function extractOrgSlugFromAttr(value: string | null): string | null {
    if (!value) return null
    try {
      const url = new URL(value, window.location.href)
      const segments = url.pathname.split('/').filter(Boolean)
      // Expected shape: /give/<orgSlug>/campaigns/<slug> or /give/<orgSlug>/forms/<slug>
      if (segments[0] === 'give' && segments[1]) {
        return segments[1]
      }
    } catch {
      // ignore
    }
    return null
  }

  function findOrgSlugOnPage(): string | null {
    const candidates: Array<string | null> = []
    document.querySelectorAll<HTMLElement>(`[${POPUP_ATTR}]`).forEach((el) =>
      candidates.push(extractOrgSlugFromAttr(el.getAttribute(POPUP_ATTR))),
    )
    document.querySelectorAll<HTMLElement>(`[${INLINE_ATTR}]`).forEach((el) =>
      candidates.push(extractOrgSlugFromAttr(el.getAttribute(INLINE_ATTR))),
    )
    document.querySelectorAll<HTMLElement>(`[${FLOAT_ATTR}]`).forEach((el) =>
      candidates.push(extractOrgSlugFromAttr(el.getAttribute(FLOAT_ATTR))),
    )
    return candidates.find((slug): slug is string => Boolean(slug)) ?? null
  }

  type ProofPayload = { enabled?: boolean; position?: ProofPosition; donations?: ProofDonation[] }

  // Classic embeds (popup button, inline form, card): the org comes from their
  // URLs and the toast feed is fetched on its own.
  async function startSocialProofRotation() {
    if (!CONVEX_SITE_URL) return

    const orgSlug = findOrgSlugOnPage()
    if (!orgSlug) return

    try {
      const response = await fetch(
        `${CONVEX_SITE_URL}/public/recent-donations?organizationSlug=${encodeURIComponent(orgSlug)}&limit=12`,
        { method: 'GET', credentials: 'omit' },
      )
      if (!response.ok) return
      runProofRotation((await response.json()) as ProofPayload)
    } catch {
      // Network trouble — the page simply shows no toasts.
    }
  }

  let proofRotationStarted = false

  function runProofRotation(payload: ProofPayload) {
    if (proofRotationStarted) return
    // Respect the org's "Recent donation toast" toggle — when it's off the
    // endpoint reports enabled:false and returns no donations.
    if (!payload.enabled) return
    const position: ProofPosition = payload.position === 'bottom-right' ? 'bottom-right' : 'bottom-left'
    const donations = Array.isArray(payload.donations) ? payload.donations : []
    if (donations.length === 0) return
    proofRotationStarted = true

    const donateUrl = findDonateUrlOnPage()
    let index = 0
    let isFirst = true

    function scheduleNext() {
      const delay = isFirst
        ? SOCIAL_PROOF_FIRST_DELAY_MS
        : SOCIAL_PROOF_MIN_GAP_MS +
          Math.round(Math.random() * (SOCIAL_PROOF_MAX_GAP_MS - SOCIAL_PROOF_MIN_GAP_MS))
      isFirst = false
      window.setTimeout(() => {
        // Cycle through the donations, looping back to the start so the toast
        // keeps rotating for as long as the donor stays on the page.
        const donation = donations[index % donations.length]
        index += 1
        showProofToast(donation, position, donateUrl)
        scheduleNext()
      }, delay)
    }

    scheduleNext()
  }

  function showThanksToast(args: { title: string; message: string }) {
    ensureStyles()

    // Remove any existing toast first so a re-render or double-fire doesn't stack.
    const existing = document.querySelector('[data-kindlumen-thanks="true"]')
    if (existing) existing.remove()

    const toast = document.createElement('div')
    toast.className = 'kindlumen-thanks-toast'
    toast.setAttribute('role', 'status')
    toast.setAttribute('aria-live', 'polite')
    toast.dataset.kindlumenThanks = 'true'

    const icon = document.createElement('div')
    icon.className = 'kindlumen-thanks-icon'
    icon.textContent = '✓'

    const body = document.createElement('div')
    body.className = 'kindlumen-thanks-body'

    const title = document.createElement('p')
    title.className = 'kindlumen-thanks-title'
    title.textContent = args.title

    const message = document.createElement('p')
    message.className = 'kindlumen-thanks-message'
    message.textContent = args.message

    body.appendChild(title)
    body.appendChild(message)

    const close = document.createElement('button')
    close.type = 'button'
    close.className = 'kindlumen-thanks-close'
    close.textContent = '×'
    close.setAttribute('aria-label', 'Dismiss')

    let dismissed = false
    function dismiss() {
      if (dismissed) return
      dismissed = true
      toast.classList.remove('kindlumen-open')
      window.setTimeout(() => toast.remove(), 320)
    }

    close.addEventListener('click', dismiss)

    toast.appendChild(icon)
    toast.appendChild(body)
    toast.appendChild(close)
    document.body.appendChild(toast)
    window.requestAnimationFrame(() => toast.classList.add('kindlumen-open'))

    // Auto-dismiss after 8 seconds. The donor can still tap × earlier.
    window.setTimeout(dismiss, 8000)
  }

  function handleDonationReturn() {
    try {
      const url = new URL(window.location.href)
      const result = url.searchParams.get(DONATION_RESULT_PARAM)
      if (!result) return

      // Clean the kindlumen-specific query params out of the visible URL so
      // a refresh doesn't re-trigger the toast.
      const cleanedUrl = new URL(window.location.href)
      cleanedUrl.searchParams.delete(DONATION_RESULT_PARAM)
      cleanedUrl.searchParams.delete('paymentSession')
      cleanedUrl.searchParams.delete('provider')
      cleanedUrl.searchParams.delete('checkoutResult')
      cleanedUrl.searchParams.delete('session_id')
      const cleanedSearch = cleanedUrl.searchParams.toString()
      const cleanedHref = `${cleanedUrl.origin}${cleanedUrl.pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}${cleanedUrl.hash}`
      window.history.replaceState(window.history.state, '', cleanedHref)

      if (result === 'success') {
        showThanksToast({
          title: 'Thank you for your donation!',
          message: 'Your gift was received. A receipt is on its way to your email.',
        })
      } else if (result === 'cancel') {
        showThanksToast({
          title: 'Donation canceled',
          message: 'No worries — no charge was made. You can try again whenever you’re ready.',
        })
      }
    } catch {
      // Malformed URL — nothing to show.
    }
  }

  function originOf(url: string): string | null {
    try {
      return new URL(url, window.location.href).origin
    } catch {
      return null
    }
  }

  // The embeddable campaign card lives at {campaign page}/card.
  function isCardUrl(url: string): boolean {
    try {
      return /\/card\/?$/.test(new URL(url, window.location.href).pathname)
    } catch {
      return false
    }
  }

  // Card URL → the campaign page it belongs to (what the popup should open).
  function toPopupUrl(url: string): string {
    try {
      const nextUrl = new URL(url, window.location.href)
      nextUrl.pathname = nextUrl.pathname.replace(/\/card\/?$/, '')
      return nextUrl.toString()
    } catch {
      return url
    }
  }

  // WordPress opts in; an unsupported page language selects the org default.
  function pageLanguage(): string | undefined {
    const script = document.querySelector<HTMLScriptElement>('script[data-kindlumen-language]')
    if (!script) return undefined
    const rendered = document.documentElement.lang
    const source = !rendered || rendered.toLowerCase() === 'auto'
      ? script.getAttribute('data-kindlumen-language') : rendered
    const hint = (source || '').trim().toLowerCase().replace(/_/g, '-')
    return supportedOrganizationLocale(hint)?.toLowerCase() ?? ''
  }

  function announceHost(frame: HTMLIFrameElement) {
    const origin = originOf(frame.src)
    if (!frame.contentWindow || !origin) return
    try {
      frame.contentWindow.postMessage({
        source: 'kindlumen',
        type: 'kindlumen:host-ready',
        capabilities: { popup: true },
        nested: window.top !== window,
        version: WIDGET_VERSION,
        languageCode: pageLanguage(),
      }, origin)
    } catch {
      // A frame may have been removed while a language update was queued.
    }
  }

  // Track only frames this widget creates, including popups opened through its API.
  const languageFrames = new Set<HTMLIFrameElement>()
  const languageObserver = new MutationObserver(() => {
    languageFrames.forEach((frame) => {
      if (frame.isConnected) announceHost(frame)
      else languageFrames.delete(frame)
    })
  })
  languageObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] })
  function trackLanguage(frame: HTMLIFrameElement) {
    languageFrames.forEach((existing) => {
      if (!existing.isConnected) languageFrames.delete(existing)
    })
    languageFrames.add(frame)
    frame.addEventListener('load', () => announceHost(frame))
  }

  function withQuery(url: string, key: string, value: string) {
    try {
      const nextUrl = new URL(url, window.location.href)
      nextUrl.searchParams.set(key, value)
      const language = pageLanguage()
      if (language !== undefined) {
        if (language) nextUrl.searchParams.set('language', language)
        else nextUrl.searchParams.delete('language')
      }
      return nextUrl.toString()
    } catch {
      return url
    }
  }

  function openModal(url: string, options?: { returnFocusTo?: HTMLElement | null }) {
    ensureStyles()
    const existingModal = document.querySelector('[data-kindlumen-modal="true"]')
    if (existingModal) existingModal.remove()

    const backdrop = document.createElement('div')
    backdrop.className = 'kindlumen-modal-backdrop'
    backdrop.dataset.kindlumenModal = 'true'

    const shell = document.createElement('div')
    shell.className = 'kindlumen-modal-shell'

    // Loader is visible immediately while the iframe boots so the donor sees
    // a spinner instead of a blank white modal. We hide it (and fade the
    // iframe in) only after the embedded page posts `kindlumen:popup-ready`.
    const loader = document.createElement('div')
    loader.className = 'kindlumen-modal-loader'
    loader.setAttribute('aria-hidden', 'true')
    const spinner = document.createElement('div')
    spinner.className = 'kindlumen-modal-spinner'
    loader.appendChild(spinner)

    const frame = document.createElement('iframe')
    frame.className = 'kindlumen-modal-frame'
    frame.src = withQuery(url, 'embed', 'popup')
    trackLanguage(frame)
    frame.title = 'KindLumen donation experience'
    // Apple Pay / Google Pay run on the W3C Payment Request API, which a
    // cross-origin iframe can only use when the embedder delegates the `payment`
    // permission. Without this, wallets are silently blocked inside the embed.
    frame.allow = 'payment'

    let contentRevealed = false
    // Declared with `var`-like hoisting semantics via a holder so the fallback
    // timer can be cleared from `revealContent` before it's assigned below.
    const fallback: { timer?: number } = {}
    function revealContent() {
      if (contentRevealed) return
      contentRevealed = true
      shell.classList.add('kindlumen-content-ready')
      window.removeEventListener('message', handleMessage)
      if (fallback.timer !== undefined) window.clearTimeout(fallback.timer)
    }
    function handleMessage(event: MessageEvent) {
      if (event.source !== frame.contentWindow) return
      const data = event.data as { type?: unknown } | null
      if (data && data.type === 'kindlumen:popup-ready') {
        announceHost(frame)
        revealContent()
      }
    }
    window.addEventListener('message', handleMessage)
    // Long fallback so we don't reveal a blank iframe before React has
    // hydrated on slow connections. The previous 1.5s window was racing the
    // iframe's `load` event and caused the intermittent white-modal flash.
    fallback.timer = window.setTimeout(revealContent, 8000)

    const close = document.createElement('button')
    close.type = 'button'
    close.className = 'kindlumen-modal-close'
    close.textContent = '×'
    close.setAttribute('aria-label', 'Close')

    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    let isClosing = false

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') handleClose()
    }
    // Escape pressed while focus is inside the iframe never reaches this
    // document; the embedded page relays it as `kindlumen:close`.
    function handleCloseMessage(event: MessageEvent) {
      if (event.source !== frame.contentWindow) return
      const data = event.data as { type?: unknown } | null
      if (data && data.type === 'kindlumen:close') handleClose()
    }

    function handleClose() {
      if (isClosing) return
      isClosing = true
      backdrop.classList.remove('kindlumen-open')
      shell.classList.remove('kindlumen-open')
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('message', handleCloseMessage)
      if (fallback.timer !== undefined) window.clearTimeout(fallback.timer)
      window.setTimeout(() => {
        backdrop.remove()
        document.body.style.overflow = previousBodyOverflow
        document.documentElement.style.overflow = previousHtmlOverflow
        document.removeEventListener('keydown', handleEscape)
        // Hand focus back to whatever opened the modal (e.g. a campaign card
        // frame) so keyboard users don't land at the top of the page.
        const focusTarget = options?.returnFocusTo
        if (focusTarget && typeof focusTarget.focus === 'function') {
          try {
            focusTarget.focus()
          } catch {
            // ignore — focus is a courtesy
          }
        }
      }, 240)
    }

    close.addEventListener('click', handleClose)
    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) handleClose()
    })
    document.addEventListener('keydown', handleEscape)
    window.addEventListener('message', handleCloseMessage)

    // Order matters: loader behind, frame on top (it'll fade in once ready),
    // close button last so it stays clickable at all times.
    shell.appendChild(loader)
    shell.appendChild(frame)
    shell.appendChild(close)
    backdrop.appendChild(shell)
    document.body.appendChild(backdrop)
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    // Reveal backdrop + shell immediately so the donor sees the spinner. The
    // iframe stays hidden until popup-ready fires (or the 8s fallback hits).
    window.requestAnimationFrame(() => {
      backdrop.classList.add('kindlumen-open')
      shell.classList.add('kindlumen-open')
    })
  }

  const reportedInstallations = new Set<string>()
  function reportInstallation(value: string) {
    if (!CONVEX_SITE_URL || new URLSearchParams(window.location.search).has('preview')) return
    let url: URL
    try { url = new URL(value, window.location.href) } catch { return }
    if (url.searchParams.has('preview') || url.origin === window.location.origin) return
    const slug = extractOrgSlugFromAttr(value)
    if (!slug || reportedInstallations.has(slug)) return
    const host = window.location.hostname
    if (!host.includes('.') || host === 'kindlumen.com' || host.endsWith('.kindlumen.com') ||
      host.endsWith('.vercel.app') || host.endsWith('.localhost') || /^\d+(\.\d+){3}$/.test(host)) return
    reportedInstallations.add(slug)
    const key = `kindlumen:installation:${slug}`
    try { if (Date.now() - Number(sessionStorage.getItem(key)) < 86400000) return } catch { /* Optional throttle. */ }
    void fetch(`${CONVEX_SITE_URL}/public/embed-observed`, {
      method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ organizationSlug: slug }).toString(),
    }).then(response => {
      if (response.ok) { try { sessionStorage.setItem(key, String(Date.now())) } catch { /* Optional throttle. */ } }
    }).catch(() => { /* Installation and donations work even when detection is blocked. */ })
  }

  function mountPopupTrigger(element: Element) {
    if (!(element instanceof HTMLElement)) return
    if (element.getAttribute(MOUNTED_ATTR) === 'true') return
    const url = element.getAttribute(POPUP_ATTR)
    if (!url) return
    element.setAttribute(MOUNTED_ATTR, 'true')
    reportInstallation(url)
    element.addEventListener('click', (event) => {
      event.preventDefault()
      openModal(url)
    })
  }

  function mountInline(element: Element) {
    if (!(element instanceof HTMLElement)) return
    if (element.getAttribute(MOUNTED_ATTR) === 'true') return
    const url = element.getAttribute(INLINE_ATTR)
    if (!url) return
    element.setAttribute(MOUNTED_ATTR, 'true')
    ensureStyles()
    const isCard = isCardUrl(url)
    const frameOrigin = originOf(url)
    const frame = document.createElement('iframe')
    frame.className = isCard ? 'kindlumen-inline-frame kindlumen-card-frame' : 'kindlumen-inline-frame'
    frame.src = withQuery(url, 'embed', 'inline')
    trackLanguage(frame)
    frame.title = isCard ? 'KindLumen campaign card' : 'KindLumen donation form'
    if (isCard) {
      // The card never takes payment itself (Donate opens the popup), so it
      // gets no payment permission; no scrollbar flash before the first resize.
      frame.setAttribute('scrolling', 'no')
    } else {
      // Delegate the `payment` permission so Apple Pay / Google Pay (Payment
      // Request API) can run inside this cross-origin iframe. See popup frame.
      frame.allow = 'payment'
    }
    frame.setAttribute('allowtransparency', 'true')

    // Tell the frame what this host can do. Sent in reply to every
    // `kindlumen:loaded` so a re-render inside the frame re-learns it.
    function replyHostReady() {
      announceHost(frame)
    }

    // Only messages from this frame's own window are honored.
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.source !== frame.contentWindow) return
      const data = event.data as { source?: unknown; type?: unknown; height?: unknown; url?: unknown } | null
      if (!data || data.source !== 'kindlumen') return

      if (data.type === 'kindlumen:loaded') {
        if (event.origin === frameOrigin) reportInstallation(url)
        replyHostReady()
        return
      }

      // The embedded page reports its content height so the iframe fits
      // exactly — no inner scrollbar, no blank space below. Each resize also
      // re-announces the host: the frame's content (and its listeners) may
      // mount after the first `loaded`, so this keeps the handshake robust.
      if (data.type === 'kindlumen:resize') {
        replyHostReady()
        const height = typeof data.height === 'number' ? data.height : Number(data.height)
        if (!Number.isFinite(height) || height <= 0) return
        frame.style.height = `${Math.ceil(height)}px`
        frame.classList.add('kindlumen-sized')
        return
      }

      // A campaign card's Donate button asks the host to open the popup. The
      // URL must come from the same origin as the frame we mounted.
      if (data.type === 'kindlumen:open-popup') {
        if (!frameOrigin || event.origin !== frameOrigin) return
        const target = typeof data.url === 'string' ? data.url : ''
        let parsed: URL | null = null
        try {
          parsed = new URL(target)
        } catch {
          parsed = null
        }
        if (!parsed || parsed.origin !== frameOrigin) return
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return
        openModal(parsed.toString(), { returnFocusTo: frame })
      }
    })
    element.innerHTML = ''
    element.appendChild(frame)
  }

  /* ---------- floating Donate button ---------- */

  type FloatConfig = {
    url: string
    label: string
    sublabel: string
    color: string
    textColor: string | null
    hoverColor: string | null
    style: 'solid' | 'outline'
    position: FloatPosition
    vertical: boolean
    shape: 'pill' | 'rounded' | 'square' | 'tab'
    size: 'sm' | 'md' | 'lg'
    font: 'system' | 'inherit'
    shadow: boolean
    offsetX: number | null
    offsetY: number | null
    icon: string | null
    hideOnMobile: boolean
  }

  // Whole pixels between 0 and 400; anything else means "use the default".
  function parseFloatOffset(value: string | undefined): number | null {
    if (value === undefined) return null
    const parsed = Number.parseFloat(value)
    if (!Number.isFinite(parsed)) return null
    return Math.min(400, Math.max(0, Math.round(parsed)))
  }

  function isHexColor(value: string | undefined): value is string {
    return typeof value === 'string' && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())
  }

  // White or near-black text, whichever reads better on the button color
  // (the same rule the app applies to form branding).
  function contrastText(hex: string): string {
    const raw = hex.trim().slice(1)
    const expanded = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw
    const channel = (part: string) => {
      const s = Number.parseInt(part, 16) / 255
      return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
    }
    const luminance =
      0.2126 * channel(expanded.slice(0, 2)) + 0.7152 * channel(expanded.slice(2, 4)) + 0.0722 * channel(expanded.slice(4, 6))
    return luminance > 0.45 ? '#0F172A' : '#FFFFFF'
  }

  function floatIconSvg(icon: string): string {
    const glyph = FLOAT_ICON_GLYPHS[icon]
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${glyph.filled ? 'currentColor' : 'none'}" ${FLOAT_ICON_SVG_ATTRIBUTES}>${glyph.markup}</svg>`
  }

  type FloatOptionReader = (key: string) => string | undefined

  // Builds a config from raw option strings — the snippet's data-* attributes
  // or the design KindLumen serves for the site-wide button — falling back
  // field by field so nothing can break the button. Same rules as
  // normalizeFloatDesign in src/lib/floating-button-spec.ts.
  function buildFloatConfig(url: string, read: FloatOptionReader): FloatConfig {
    const rawPosition = read('position') ?? ''
    const position = (FLOAT_POSITIONS as readonly string[]).includes(rawPosition) ? (rawPosition as FloatPosition) : 'bottom-right'
    const vertical = position === 'left' || position === 'right'
    const iconKey = (read('icon') ?? 'heart').trim().toLowerCase()
    const knownIcon = Object.prototype.hasOwnProperty.call(FLOAT_ICON_GLYPHS, iconKey)
    const shape = read('shape')
    const size = read('size')
    const color = read('color')
    const textColor = read('textColor')
    const hoverColor = read('hoverColor')
    return {
      url,
      label: (read('label') ?? '').trim().slice(0, 40) || 'Donate',
      sublabel: (read('sublabel') ?? '').trim().slice(0, 60),
      color: isHexColor(color) ? color.trim() : FLOAT_DEFAULT_COLOR,
      textColor: isHexColor(textColor) ? textColor.trim() : null,
      hoverColor: isHexColor(hoverColor) ? hoverColor.trim() : null,
      style: read('style') === 'outline' ? 'outline' : 'solid',
      position,
      vertical,
      shape:
        shape === 'pill' || shape === 'rounded' || shape === 'square' || shape === 'tab' ? shape : vertical ? 'tab' : 'pill',
      size: size === 'sm' || size === 'lg' ? size : 'md',
      font: read('font') === 'inherit' ? 'inherit' : 'system',
      shadow: read('shadow') !== 'none',
      offsetX: parseFloatOffset(read('offsetX')),
      offsetY: parseFloatOffset(read('offsetY')),
      icon: iconKey === 'none' ? null : knownIcon ? iconKey : 'heart',
      hideOnMobile: read('mobile') === 'hide',
    }
  }

  function readFloatConfig(element: HTMLElement): FloatConfig | null {
    const url = element.getAttribute(FLOAT_ATTR)
    if (!url) return null
    return buildFloatConfig(url, (key) => element.dataset[key])
  }

  // The design KindLumen serves for the site-wide button, read as option strings.
  function floatConfigFromDesign(url: string, design: Record<string, unknown>): FloatConfig {
    return buildFloatConfig(url, (key) => {
      const value = design[key]
      if (key === 'shadow') return value === false ? 'none' : undefined
      if (typeof value === 'number') return String(value)
      return typeof value === 'string' ? value : undefined
    })
  }

  // What the button is painted with (same rules as resolveFloatPalette in
  // src/lib/floating-button.ts): text defaults to whichever of white /
  // near-black reads best, and outline buttons fill with their color on hover.
  function floatPalette(config: FloatConfig) {
    const hoverBackground = config.hoverColor ?? config.color
    if (config.style === 'outline') {
      return {
        background: 'transparent',
        text: config.textColor ?? config.color,
        border: config.color,
        hoverBackground,
        hoverText: contrastText(hoverBackground),
      }
    }
    return {
      background: config.color,
      text: config.textColor ?? contrastText(config.color),
      border: null,
      hoverBackground,
      hoverText: config.textColor ?? contrastText(hoverBackground),
    }
  }

  function mountFloat(element: Element) {
    if (!(element instanceof HTMLElement)) return
    if (element.getAttribute(MOUNTED_ATTR) === 'true') return
    const config = readFloatConfig(element)
    if (!config) return
    element.setAttribute(MOUNTED_ATTR, 'true')
    // The carrier only holds the options. The button is appended to <body>
    // so an ancestor with transform/overflow can't trap a fixed element.
    if (!(element instanceof HTMLScriptElement)) element.hidden = true
    renderFloat(config)
  }

  function renderFloat(config: FloatConfig) {
    ensureStyles()

    const host = document.createElement('div')
    host.className = [
      'kindlumen-float-host',
      `kindlumen-float-${config.position}`,
      `kindlumen-float-${config.shape}`,
      `kindlumen-float-${config.size}`,
      config.vertical ? 'kindlumen-float-vertical' : '',
      config.position === 'left' ? 'kindlumen-float-flip' : '',
      config.font === 'inherit' ? 'kindlumen-float-font-inherit' : '',
      config.shadow ? '' : 'kindlumen-float-no-shadow',
      config.hideOnMobile ? 'kindlumen-float-hide-mobile' : '',
    ]
      .filter(Boolean)
      .join(' ')
    host.dataset.kindlumenFloatHost = 'true'
    if (config.offsetX !== null) host.style.setProperty('--kl-float-gap-x', `${config.offsetX}px`)
    if (config.offsetY !== null) host.style.setProperty('--kl-float-gap-y', `${config.offsetY}px`)

    const palette = floatPalette(config)
    const button = document.createElement('a')
    button.className = 'kindlumen-float-button'
    button.href = config.url
    button.style.setProperty('--kl-float-bg', palette.background)
    button.style.setProperty('--kl-float-fg', palette.text)
    button.style.setProperty('--kl-float-hover-bg', palette.hoverBackground)
    button.style.setProperty('--kl-float-hover-fg', palette.hoverText)
    if (palette.border) button.style.setProperty('--kl-float-border', `2px solid ${palette.border}`)
    button.setAttribute('aria-haspopup', 'dialog')

    const inner = document.createElement('span')
    inner.className = 'kindlumen-float-inner'
    if (config.icon) {
      const icon = document.createElement('span')
      icon.className = 'kindlumen-float-icon'
      icon.innerHTML = floatIconSvg(config.icon)
      inner.appendChild(icon)
    }
    const text = document.createElement('span')
    text.className = 'kindlumen-float-text'
    const label = document.createElement('span')
    label.className = 'kindlumen-float-label'
    label.textContent = config.label
    text.appendChild(label)
    if (config.sublabel) {
      const sublabel = document.createElement('span')
      sublabel.className = 'kindlumen-float-sublabel'
      sublabel.textContent = config.sublabel
      text.appendChild(sublabel)
    }
    inner.appendChild(text)
    button.appendChild(inner)
    host.appendChild(button)

    if (window.top !== window) {
      // Inside another frame (a builder's HTML box, a preview canvas) the
      // popup couldn't cover the page, so open the hosted page in a new tab.
      button.target = '_blank'
      button.rel = 'noopener'
    } else {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        openModal(config.url, { returnFocusTo: button })
      })
    }

    document.body.appendChild(host)
    reportInstallation(config.url)
    // Lets the toasts move out of the button's corner (see ensureStyles and
    // showProofToast). A button hidden on phones needs no room there.
    document.documentElement.classList.add(`kindlumen-has-float-${config.position}`)
    if (config.hideOnMobile) document.documentElement.classList.add('kindlumen-float-mobile-hidden')
    window.requestAnimationFrame(() => host.classList.add('kindlumen-float-in'))
  }

  /* ---------- site-wide snippet (data-kindlumen-site) ---------- */

  let siteWidgetStarted = false

  function findSiteSlugOnPage(): string | null {
    const carrier = document.querySelector(`[${SITE_ATTR}]`)
    const slug = carrier?.getAttribute(SITE_ATTR)?.trim() ?? ''
    return /^[a-z0-9][a-z0-9-]{0,79}$/i.test(slug) ? slug : null
  }

  function isHttpUrl(value: string): boolean {
    try {
      const parsed = new URL(value)
      return parsed.protocol === 'https:' || parsed.protocol === 'http:'
    } catch {
      return false
    }
  }

  // One request for everything the org set up for its website: the floating
  // Donate button (rendered here from the served design) and the toast feed.
  async function startSiteWidget(orgSlug: string) {
    if (siteWidgetStarted || !CONVEX_SITE_URL) return
    siteWidgetStarted = true

    let payload: { floatingButton?: { enabled?: boolean; url?: unknown; design?: unknown }; socialProof?: ProofPayload }
    try {
      // Always fresh, so a change made in KindLumen shows on the next page load.
      const response = await fetch(
        `${CONVEX_SITE_URL}/public/site-widget?organizationSlug=${encodeURIComponent(orgSlug)}&limit=12`,
        { method: 'GET', credentials: 'omit', cache: 'no-store' },
      )
      if (!response.ok) return
      payload = (await response.json()) as typeof payload
    } catch {
      return
    }

    const floating = payload.floatingButton
    // A hand-placed data-kindlumen-float button on the same page wins.
    if (
      floating?.enabled &&
      typeof floating.url === 'string' &&
      isHttpUrl(floating.url) &&
      !document.querySelector('[data-kindlumen-float-host]')
    ) {
      const design = floating.design && typeof floating.design === 'object' ? (floating.design as Record<string, unknown>) : {}
      renderFloat(floatConfigFromDesign(floating.url, design))
    }
    if (payload.socialProof) runProofRotation(payload.socialProof)
  }

  function scan(root: ParentNode = document) {
    root.querySelectorAll<HTMLElement>(`[${POPUP_ATTR}]`).forEach(mountPopupTrigger)
    root.querySelectorAll<HTMLElement>(`[${INLINE_ATTR}]`).forEach(mountInline)
    root.querySelectorAll<HTMLElement>(`[${FLOAT_ATTR}]`).forEach(mountFloat)
  }

  function start() {
    scan()
    // Surface a thank-you (or canceled) message if the donor just came back
    // from Stripe Checkout. Runs once on script start since the redirect
    // produces a full page load on the org's site.
    handleDonationReturn()
    // A site-wide snippet names the organization: its floating button and
    // toast feed arrive together. Otherwise the classic embeds' URLs name the
    // org and only the toasts are fetched.
    const siteSlug = findSiteSlugOnPage()
    if (siteSlug) void startSiteWidget(siteSlug)
    else void startSocialProofRotation()
    // Catch anything added to the DOM after the script loads.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return
          if (node.hasAttribute?.(POPUP_ATTR)) mountPopupTrigger(node)
          else if (node.hasAttribute?.(INLINE_ATTR)) mountInline(node)
          else if (node.hasAttribute?.(FLOAT_ATTR)) mountFloat(node)
          else if (node.hasAttribute?.(SITE_ATTR)) {
            const slug = findSiteSlugOnPage()
            if (slug) void startSiteWidget(slug)
          }
          scan(node)
        })
      }
    })
    // Watch the whole document (not just <body>): tag managers sometimes drop
    // the floating-button snippet into <head>.
    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start)
  } else {
    start()
  }

  // Expose a tiny API for custom triggers, e.g. KindLumen.open(url)
  window.KindLumen = { open: openModal, version: WIDGET_VERSION }
})()
